/**
 * AvailabilityModal
 * Modal to add/edit availability blocks (PTO, travel, etc.)
 */

import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createAvailability,
  updateAvailability,
  deleteAvailability,
} from "../../services/salesCalendar.service";
import { useAuth } from "../../contexts/AuthContext";
import type {
  SalesRepAvailability,
  AvailabilityType,
} from "../../types/salesCalendar.types";
import { AVAILABILITY_TYPE_LABELS } from "../../types/salesCalendar.types";
import type { SalesPosition } from "../../types/sales.types";
import Button from "../common/Button";
import { XMarkIcon } from "@heroicons/react/24/outline";

interface AvailabilityModalProps {
  onClose: () => void;
  positions: SalesPosition[];
  /** Pre-selected position (for per-rep views) */
  preSelectedPositionId?: string;
  /** Pre-selected start date (from calendar slot click) */
  preSelectedDate?: string;
  /** Existing record to edit */
  existing?: SalesRepAvailability;
}

const AVAILABILITY_TYPES: AvailabilityType[] = [
  "pto",
  "unavailable",
  "training",
  "travel",
  "out_of_town",
];

const AvailabilityModal: React.FC<AvailabilityModalProps> = ({
  onClose,
  positions,
  preSelectedPositionId,
  preSelectedDate,
  existing,
}) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isEdit = !!existing;

  const [positionId, setPositionId] = useState(
    existing?.sales_position_id || preSelectedPositionId || "",
  );
  const [startDate, setStartDate] = useState(
    existing?.start_date || preSelectedDate || "",
  );
  const [endDate, setEndDate] = useState(
    existing?.end_date || preSelectedDate || "",
  );
  const [availType, setAvailType] = useState<AvailabilityType>(
    existing?.availability_type || "pto",
  );
  const [title, setTitle] = useState(existing?.title || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  // MSR QA notes 2026-10-19, item 3: availability is set by whole day only (reps and managers alike). A block saved
  // earlier with times still displays, and saving it here makes it whole-day.
  const wasTimed = existing?.is_all_day === false;
  const [error, setError] = useState<string | null>(null);

  const saveMutation = useMutation({
    mutationFn: () => {
      // Whole days only: clears any times a legacy timed block carried.
      const timeFields = {
        is_all_day: true,
        start_time: null,
        end_time: null,
      };
      const effEndDate = endDate;
      if (isEdit) {
        return updateAvailability(existing.id, {
          start_date: startDate,
          end_date: effEndDate,
          availability_type: availType,
          title: title || undefined,
          notes: notes || undefined,
          ...timeFields,
        });
      }
      return createAvailability(
        {
          sales_position_id: positionId,
          start_date: startDate,
          end_date: effEndDate,
          availability_type: availType,
          title: title || undefined,
          notes: notes || undefined,
          ...timeFields,
        },
        user?.id,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-calendar"] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteAvailability(existing!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["sales-calendar"] });
      onClose();
    },
    onError: (err: Error) => setError(err.message),
  });

  const handleSave = () => {
    if (!positionId) {
      setError("Please select a rep");
      return;
    }
    if (!startDate || !endDate) {
      setError("Start and end dates are required");
      return;
    }
    if (endDate < startDate) {
      setError("End date must be on or after start date");
      return;
    }
    setError(null);
    saveMutation.mutate();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">
            {isEdit ? "Edit Availability" : "Add Availability"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-4 space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Rep selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sales Rep *
            </label>
            <select
              value={positionId}
              onChange={(e) => setPositionId(e.target.value)}
              disabled={isEdit}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm disabled:bg-gray-100"
            >
              <option value="">Select a rep...</option>
              {positions.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.assigned_name || p.title}
                </option>
              ))}
            </select>
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type *
            </label>
            <select
              value={availType}
              onChange={(e) => setAvailType(e.target.value as AvailabilityType)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              {AVAILABILITY_TYPES.map((t) => (
                <option key={t} value={t}>
                  {AVAILABILITY_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          {wasTimed && (
            <p className="rounded bg-amber-50 p-2 text-xs text-amber-800">
              This block was saved with times. Availability is now set by whole
              day, so saving it covers the whole day.
            </p>
          )}

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date *
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Vacation, Conference, etc."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t">
          <div>
            {isEdit && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving..." : isEdit ? "Update" : "Add"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityModal;
